import type { RegionLatency } from './measure'
import type { LatencyState } from './use-latency'

/*
 * V1_2_SPEC.md §10.4 — how a latency measurement is presented.
 *
 *   Europe West       28 ms   Fastest for you
 *   Europe Central    41 ms
 *
 * The four rules that are easy to get wrong, and are each one line of code:
 *
 *   a failure reads as a failure — never a missing row, never a zero (§10.4)
 *   a region that is not operational shows its **state**, not a number
 *   "Fastest for you" only on a ≥ 15 % lead — a 2 ms gap is noise
 *   the badge changes nothing: cluster selection is a deliberate product action
 *
 * That last one is the reason the section carries a sentence saying so. A badge
 * on an infrastructure page reads as advice, and §10.4 is explicit that this
 * measurement moves no application: choosing a cluster happens at creation, and
 * migrating one is a sensitive admin operation.
 */

/** §10.1's wording, and the page never says "ping" anywhere. */
export const LATENCY_INTRO =
  'Network latency estimate — round-trip time from your browser to each region over the ' +
  'same public path your WebSocket traffic takes. Not a datacenter ping, and it changes ' +
  'nothing about where your applications run.'

function byCode(results: readonly RegionLatency[]): Map<string, RegionLatency> {
  return new Map(results.map((row) => [row.code, row]))
}

export function LatencyCell({
  state,
  code,
  operational,
}: {
  readonly state: LatencyState
  readonly code: string
  /** False for a region in maintenance, disabled or not reporting. §10.4: it
   *  "shows its state instead of a number" — which the row already does, so
   *  this renders nothing rather than a second, quieter status. */
  readonly operational: boolean
}) {
  if (!operational) return null

  if (state.kind === 'idle' || state.kind === 'measuring') {
    return (
      <span className="type-data text-xs text-text-muted">
        {state.kind === 'measuring' ? 'measuring…' : ''}
      </span>
    )
  }

  if (state.kind === 'unavailable') {
    return <span className="text-xs text-text-muted">not measured</span>
  }

  const row = byCode(state.results).get(code)
  if (row?.medianMs == null) {
    /*
     * §10.4: "A measurement failure reads as a failure. It is never a missing
     * row and never a zero." An em dash with a spoken label — the design
     * system's `NoValue` shape — rather than blank space, which a reader
     * cannot distinguish from a column that has not loaded.
     */
    return (
      <span className="text-xs text-text-muted" translate="no">
        <span aria-hidden="true">—</span>
        <span className="sr-only">no measurement</span>
      </span>
    )
  }

  return (
    <span className="flex items-baseline gap-2 text-xs">
      <span className="type-data text-text-secondary">{row.medianMs} ms</span>
      {state.fastestCode === code ? (
        <span className="text-text-muted">Fastest for you</span>
      ) : null}
    </span>
  )
}

export function LatencyControl({
  state,
  onRemeasure,
}: {
  readonly state: LatencyState
  readonly onRemeasure: () => void
}) {
  return (
    <p className="mt-2 flex flex-wrap items-baseline gap-x-3 gap-y-1 text-xs">
      <button
        type="button"
        onClick={onRemeasure}
        disabled={state.kind === 'measuring'}
        className="rounded-xs border border-border-default px-2 py-1 text-text-secondary hover:bg-surface-raised disabled:text-text-disabled"
      >
        {state.kind === 'measuring' ? 'Measuring…' : 'Re-measure'}
      </button>
      {state.kind === 'unavailable' ? (
        <span className="text-text-muted">
          Latency could not be measured from this browser.
        </span>
      ) : null}
    </p>
  )
}
